import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import LoadingOverlay from "./LoadingOverlay";
import PrintQCShipment from "./PrintQCShipment";
import { fetchFormNos } from "../utils/formNos";
import { FORM_KEYS } from "../config/formIds";

/* ── helpers ──────────────────────────────────────────────────────── */
const createFormHeader = async () => {
  const res = await axios.post(
    "https://eps.elkomprintama.com/post/api/store/request_header",
  );
  return res.data.form_id;
};

const toDateStr = (val) => (val ? String(val).split("T")[0] : null);

const doShipToFG = async ({ order, fqty, username }) =>
  axios.post("https://eps.elkomprintama.com/post/api/fin_orders/", {
    custId: order.cust_id,
    jobId: order.job_id,
    date: toDateStr(order.date),
    qty: order.qty,
    poNum: order.po_num,
    dlvry: toDateStr(order.dlvr_date),
    opr: order.opr_id,
    qc: order.qc_id,
    fdate: new Date().toISOString().split("T")[0],
    fqty,
    orderId: order.order_id,
    preparedBy: username ?? null,
  });

/* ── Confirm move to QC Check ─────────────────────────────────────── */
function ConfirmQCModal({ order, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-[520px] p-6">
        <h3 className="text-sm font-bold uppercase text-gray-700 mb-4">
          Move to QC Check?
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600 mb-4">
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Order
            </span>
            <span className="font-semibold">#{order.order_id}</span>
          </div>
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Customer
            </span>
            <span>{order.nme}</span>
          </div>
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Job Name
            </span>
            <span>{order.job_name}</span>
          </div>
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Qty
            </span>
            <span className="font-semibold">{order.qty}</span>
          </div>
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Roll
            </span>
            <span>{order.roll ?? "—"}</span>
          </div>
          <div>
            <span className="uppercase font-bold text-gray-400 block mb-0.5">
              Std Pack
            </span>
            <span>{order.std_pack ?? "—"}</span>
          </div>
        </div>
        {order.product_id ? (
          <div className="bg-green-50 border border-green-300 rounded p-3 mb-5 text-xs">
            <span className="uppercase font-bold text-gray-400 block mb-1">
              Store Product Name
            </span>
            <span className="font-semibold text-green-700 font-mono">
              [{order.product_code}]
            </span>{" "}
            <span className="text-green-800">{order.product_name}</span>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-5 text-xs text-gray-400 italic">
            No store product linked to this jobsheet.
          </div>
        )}
        <div className="flex gap-3">
          <button
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-bold py-2.5 rounded"
            onClick={onConfirm}
          >
            Confirm QC Check
          </button>
          <button
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold py-2.5 rounded"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Single order process modal ───────────────────────────────────── */
function ShipModal({ order, onClose, onShipped, username }) {
  const [action, setAction] = useState(order.product_id ? "store" : "fg");
  const [qty, setQty] = useState(order.product_id ? "" : String(order.qty));
  const [remark, setRemark] = useState("");
  const [customer, setCustomer] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleFGSubmit = async () => {
    const qcUnassigned =
      !order.qc_id || order.qc_name?.trim().toLowerCase() === "unassigned";
    if (qcUnassigned) {
      toast.error("QC must be assigned before finishing.");
      return;
    }
    const fqty = Number(qty);
    if (!fqty || fqty <= 0) {
      toast.warning("Enter a valid finish qty.");
      return;
    }
    setIsLoading(true);
    try {
      await doShipToFG({ order, fqty, username });
      toast.success("Order finished!");
      onShipped({
        type: "finish",
        preparedBy: username,
        orders: [
          {
            customerName: order.nme,
            jobName: order.job_name,
            size: order.size,
            qty: fqty,
            poNum: order.po_num,
            operator: order.opr_name,
            qc: order.qc_name,
          },
        ],
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoreSubmit = async () => {
    const shipQty = Number(qty);
    if (!shipQty || shipQty <= 0) {
      toast.warning("Enter a valid quantity.");
      return;
    }
    setIsLoading(true);
    try {
      const formId = await createFormHeader();
      const groupId = uuid();
      await axios.post("https://eps.elkomprintama.com/post/api/store/stocks", {
        transactions: [
          {
            product_id: order.product_id,
            order_id: order.order_id,
            stock_in: shipQty,
            stock_out: 0,
            transaction_type: "QC Pending Stock In",
            transaction_by: username,
            transaction_for: customer || null,
            transaction_remark: remark || null,
            transaction_group_id: groupId,
            form_id: formId,
          },
        ],
      });
      toast.success("Shipment to store recorded!");
      onShipped({
        formId,
        groupId,
        items: [
          {
            product_code: order.product_code,
            product_name: order.product_name,
            ship_qty: shipQty,
            remark: remark || "",
            customer: customer || "",
            username,
          },
        ],
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to process.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-[580px] p-6 relative">
        {isLoading && <LoadingOverlay message="Processing..." />}

        <h3 className="text-sm font-bold uppercase text-gray-700 mb-1">
          Process Order
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Order #{order.order_id} — {order.job_name} | {order.nme}
        </p>

        {/* Action selector */}
        <div className="flex gap-2 mb-4">
          {order.product_id && (
            <button
              className={`flex-1 border-2 rounded py-2 text-xs font-bold transition-colors ${
                action === "store"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-200 text-gray-500 hover:border-gray-300"
              }`}
              onClick={() => {
                setAction("store");
                setQty("");
              }}
            >
              Shipment to Store
            </button>
          )}
          <button
            className={`flex-1 border-2 rounded py-2 text-xs font-bold transition-colors ${
              action === "fg"
                ? "border-purple-500 bg-purple-50 text-purple-700"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
            onClick={() => {
              setAction("fg");
              setQty(String(order.qty));
            }}
          >
            Finish
          </button>
        </div>

        {/* Product info (store action only) */}
        {action === "store" && (
          <div className="bg-green-50 border border-green-300 rounded p-2 mb-4 text-xs">
            <span className="font-semibold text-green-700">
              [{order.product_code}]
            </span>{" "}
            <span className="text-green-800">{order.product_name}</span>
            <span className="ml-2 text-green-600 italic">
              — QR form will be printed
            </span>
          </div>
        )}

        {/* Qty — shown for both actions with contextual label */}
        <div className="mb-3">
          <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
            {action === "fg" ? "Finish Qty" : "Quantity to Ship"}
          </label>
          <input
            type="number"
            min="1"
            className="w-full border border-green-400 rounded py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
            }}
            autoFocus
          />
        </div>

        {/* Remark + Customer (store action only) */}
        {action === "store" && (
          <>
            <div className="mb-3">
              <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                Remark / Catatan
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded py-2 px-3 text-sm focus:outline-none"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Optional remark"
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                Customer / For
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded py-2 px-3 text-sm focus:outline-none"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Customer or department"
              />
            </div>
          </>
        )}

        {action === "fg" && (
          <div className="bg-purple-50 border border-purple-200 rounded p-2 mb-4 text-xs text-purple-700">
            Moves order directly to Finished Goods. No stock entry is created.
          </div>
        )}

        <button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 rounded mb-2"
          onClick={action === "fg" ? handleFGSubmit : handleStoreSubmit}
        >
          Confirm
        </button>
        <button
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-2 rounded"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ── Batch process modal ──────────────────────────────────────────── */
function BatchModal({ orders, onClose, onDone, username }) {
  const [rows, setRows] = useState(() =>
    orders.map((o) => ({
      orderId: o.order_id,
      action: o.product_id ? "store" : "fg",
      qty: o.product_id ? "" : String(o.qty),
      remark: "",
      customer: "",
    })),
  );
  const [isLoading, setIsLoading] = useState(false);

  const updateRow = (orderId, field, value) =>
    setRows((prev) =>
      prev.map((r) => (r.orderId === orderId ? { ...r, [field]: value } : r)),
    );

  const handleProcess = async () => {
    for (const row of rows) {
      const q = Number(row.qty);
      if (!q || q <= 0) {
        toast.warning(`Order #${row.orderId}: enter a valid qty.`);
        return;
      }
    }

    const fgRows = rows.filter((r) => r.action === "fg");
    for (const row of fgRows) {
      const order = orders.find((o) => o.order_id === row.orderId);
      const qcUnassigned =
        !order.qc_id || order.qc_name?.trim().toLowerCase() === "unassigned";
      if (qcUnassigned) {
        toast.error(
          `Order #${row.orderId} (${order.job_name}): QC must be assigned before finishing.`,
        );
        return;
      }
    }

    setIsLoading(true);
    let ok = 0;

    const storeRows = rows.filter((r) => r.action === "store");
    let printData = null;

    if (storeRows.length > 0) {
      try {
        const formId = await createFormHeader();
        const groupId = uuid();
        const printItems = [];

        for (const row of storeRows) {
          const order = orders.find((o) => o.order_id === row.orderId);
          const shipQty = Number(row.qty);
          try {
            await axios.post(
              "https://eps.elkomprintama.com/post/api/store/stocks",
              {
                transactions: [
                  {
                    product_id: order.product_id,
                    order_id: order.order_id,
                    stock_in: shipQty,
                    stock_out: 0,
                    transaction_type: "QC Pending Stock In",
                    transaction_by: username,
                    transaction_for: row.customer || null,
                    transaction_remark: row.remark || null,
                    transaction_group_id: groupId,
                    form_id: formId,
                  },
                ],
              },
            );
            printItems.push({
              product_code: order.product_code,
              product_name: order.product_name,
              ship_qty: shipQty,
              remark: row.remark || "",
              customer: row.customer || "",
              username,
            });
            ok++;
          } catch (err) {
            toast.error(
              `Order #${row.orderId}: ${err?.response?.data?.msg || "failed"}`,
            );
          }
        }

        if (printItems.length > 0) {
          printData = { formId, groupId, items: printItems };
        }
      } catch (err) {
        toast.error("Failed to create form header.");
      }
    }

    const finishOrders = [];
    for (const row of fgRows) {
      const order = orders.find((o) => o.order_id === row.orderId);
      const fqty = Number(row.qty);
      try {
        await doShipToFG({ order, fqty, username });
        finishOrders.push({
          customerName: order.nme,
          jobName: order.job_name,
          size: order.size,
          qty: fqty,
          poNum: order.po_num,
          operator: order.opr_name,
          qc: order.qc_name,
        });
        ok++;
      } catch (err) {
        toast.error(
          `Order #${row.orderId}: ${err?.response?.data?.msg || "failed"}`,
        );
      }
    }

    setIsLoading(false);
    if (ok > 0) {
      toast.success(`${ok} order(s) processed!`);
      onDone(
        finishOrders.length > 0
          ? { type: "finish", preparedBy: username, orders: finishOrders }
          : null,
      );
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-[1100px] max-h-[85vh] flex flex-col p-6 relative">
        {isLoading && <LoadingOverlay message="Processing..." />}
        <h3 className="text-sm font-bold uppercase text-gray-700 mb-1">
          Batch Process
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          {orders.length} order(s) selected — store shipments share one QR form.
        </p>

        <div className="overflow-y-auto flex-1 border rounded mb-4">
          <table className="text-xs w-full border-collapse">
            <thead className="bg-gray-700 text-white uppercase sticky top-0">
              <tr>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Order
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Job
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Product Code
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Store Product Name
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Part No. (Cust)
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Action
                </th>
                <th className="px-3 py-2 border-r text-center whitespace-nowrap bg-yellow-600">
                  Order Qty
                </th>
                <th className="px-3 py-2 border-r text-center whitespace-nowrap">
                  Ship Qty
                </th>
                <th className="px-3 py-2 border-r text-left whitespace-nowrap">
                  Remark
                </th>
                <th className="px-3 py-2 text-left whitespace-nowrap">
                  Customer / For
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, i) => {
                const row = rows.find((r) => r.orderId === order.order_id);
                const isStore = row.action === "store";
                return (
                  <tr
                    key={order.order_id}
                    className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="px-3 py-2 border-r font-semibold whitespace-nowrap">
                      #{order.order_id}
                    </td>
                    <td className="px-3 py-2 border-r whitespace-nowrap">
                      {order.job_name}
                    </td>
                    <td className="px-3 py-2 border-r">
                      {order.product_code ? (
                        <span className="font-mono text-green-700">
                          {order.product_code}
                        </span>
                      ) : (
                        <span className="text-red-400 italic">none</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border-r">
                      {order.product_name ? (
                        <span className="text-green-800">
                          {order.product_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border-r text-gray-600 whitespace-nowrap">
                      {order.pn_cust || "—"}
                    </td>
                    <td className="px-3 py-2 border-r">
                      <select
                        className="border border-gray-300 rounded px-1 py-0.5 text-xs bg-white"
                        value={row.action}
                        onChange={(e) => {
                          const newAction = e.target.value;
                          updateRow(order.order_id, "action", newAction);
                          updateRow(
                            order.order_id,
                            "qty",
                            newAction === "fg" ? String(order.qty) : "",
                          );
                        }}
                      >
                        {order.product_id && (
                          <option value="store">Shipment to Store</option>
                        )}
                        <option value="fg">Finish</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 border-r text-center font-bold bg-yellow-100 text-yellow-800 whitespace-nowrap">
                      {order.qty}
                    </td>
                    <td className="px-3 py-2 border-r text-center">
                      <input
                        type="number"
                        min="1"
                        className="border border-green-400 rounded px-2 py-0.5 text-xs w-20 focus:outline-none text-center"
                        value={row.qty}
                        onChange={(e) =>
                          updateRow(order.order_id, "qty", e.target.value)
                        }
                        placeholder={row.action === "fg" ? "Finish" : ""}
                      />
                    </td>
                    <td className="px-3 py-2 border-r">
                      {isStore ? (
                        <input
                          type="text"
                          className="border border-gray-300 rounded px-2 py-0.5 text-xs w-28 focus:outline-none"
                          value={row.remark}
                          onChange={(e) =>
                            updateRow(order.order_id, "remark", e.target.value)
                          }
                          placeholder="Remark"
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isStore ? (
                        <input
                          type="text"
                          className="border border-gray-300 rounded px-2 py-0.5 text-xs w-28 focus:outline-none"
                          value={row.customer}
                          onChange={(e) =>
                            updateRow(
                              order.order_id,
                              "customer",
                              e.target.value,
                            )
                          }
                          placeholder="Customer"
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex gap-3">
          <button
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 rounded"
            onClick={handleProcess}
          >
            Process All
          </button>
          <button
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-2 rounded"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main QC Checking Component ───────────────────────────────────── */
function QCChecking() {
  const [orders, setOrders] = useState([]);
  const [qcList, setQcList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("on_process");
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [shipOrder, setShipOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);
  const [qcShipments, setQcShipments] = useState([]);
  const [formNos, setFormNos] = useState({});
  const [onProcessSearch, setOnProcessSearch] = useState("");
  const { user } = useSelector((state) => state.auth);

  const fetchQcShipments = async () => {
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/transaction/qc_history",
      );
      setQcShipments(res.data.result || []);
    } catch {}
  };

  useEffect(() => {
    getQCOrders();
    fetchQcShipments();
    fetchFormNos().then(setFormNos);
    axios
      .get("https://eps.elkomprintama.com/get/api/qc")
      .then((res) => setQcList(res.data.result || []))
      .catch(() => {});
  }, []);

  const getQCOrders = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/orders/qc",
      );
      setOrders(res.data.result || []);
    } catch {
      toast.error("Failed to fetch orders.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveToQC = async () => {
    if (!confirmOrder) return;
    try {
      await axios.patch(
        `https://eps.elkomprintama.com/patch/api/orders/${confirmOrder.order_id}/qc`,
      );
      toast.success(`Order #${confirmOrder.order_id} moved to QC Check!`);
      setConfirmOrder(null);
      getQCOrders();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to update status.");
      setConfirmOrder(null);
    }
  };

  const handleQCAssign = async (orderId, qcId) => {
    try {
      await axios.patch(
        `https://eps.elkomprintama.com/patch/api/orders/${orderId}/qc_assign`,
        { qc_id: qcId || null },
      );
      setOrders((prev) =>
        prev.map((o) => {
          if (o.order_id !== orderId) return o;
          const found = qcList.find((q) => q.qc_id === Number(qcId));
          return { ...o, qc_id: qcId || null, qc_name: found?.qc_name || null };
        }),
      );
    } catch {
      toast.error("Failed to update QC assignment.");
    }
  };

  const handleShipped = (printData) => {
    getQCOrders();
    fetchQcShipments();
    if (!printData || printData.type === "finish") return;
    setPrintQueue((prev) => [...prev, { ...printData, key: uuid() }]);
  };

  const toggleSelect = (orderId) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });

  const onProcess = orders.filter((o) => o.job_status === "ON PROCESS");
  const onProcessFiltered = onProcessSearch.trim()
    ? onProcess.filter((o) => {
        const q = onProcessSearch.toLowerCase();
        return (
          o.nme?.toLowerCase().includes(q) ||
          o.job_name?.toLowerCase().includes(q) ||
          o.pn_cust?.toLowerCase().includes(q) ||
          o.po_num?.toLowerCase().includes(q) ||
          o.product_name?.toLowerCase().includes(q)
        );
      })
    : onProcess;
  const onQC = orders.filter((o) => o.job_status === "QC CHECK");
  const selectedOrders = onQC.filter((o) => selectedIds.has(o.order_id));

  // Determine whether the current selection contains orders with or without product code
  const selectionHasProduct = selectedOrders.some((o) => o.product_id);
  const selectionHasNoProduct = selectedOrders.some((o) => !o.product_id);

  // An order conflicts with the current selection if it's a different type
  const isCheckboxDisabled = (o) => {
    if (selectedIds.size === 0) return false;
    if (selectionHasProduct && !o.product_id) return true;
    if (selectionHasNoProduct && o.product_id) return true;
    return false;
  };

  // "Select all" only toggles selectable orders (same type as current selection, or all if none selected)
  const selectableQC =
    selectedIds.size === 0 ? onQC : onQC.filter((o) => !isCheckboxDisabled(o));
  const allSelectableSelected =
    selectableQC.length > 0 &&
    selectableQC.every((o) => selectedIds.has(o.order_id));

  const toggleSelectAll = () =>
    allSelectableSelected
      ? setSelectedIds(new Set())
      : setSelectedIds(new Set(selectableQC.map((o) => o.order_id)));

  function stripedRow(i) {
    return i % 2 === 0 ? "bg-gray-50" : "bg-white";
  }

  const sharedThead = (withCheckbox = false) => (
    <thead className="sticky top-0 text-xs text-white uppercase bg-gray-700">
      <tr>
        {withCheckbox && (
          <th className="px-3 py-3 border-r">
            <input
              type="checkbox"
              checked={allSelectableSelected}
              onChange={toggleSelectAll}
              className="cursor-pointer"
            />
          </th>
        )}
        <th className="px-4 py-3 border-r whitespace-nowrap">Customer</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Job Name</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">
          Store Product Name
        </th>
        <th className="px-4 py-3 border-r whitespace-nowrap">
          Part No. (Cust)
        </th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Size</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Roll</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Std Pack</th>
        <th className="px-4 py-3 border-r whitespace-nowrap bg-yellow-600">
          Qty
        </th>
        <th className="px-4 py-3 border-r whitespace-nowrap">PO No.</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Delivery Date</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">Operator</th>
        <th className="px-4 py-3 border-r whitespace-nowrap">QC</th>
        <th className="px-4 py-3 whitespace-nowrap">Product Code</th>
      </tr>
    </thead>
  );

  const productCell = (o) =>
    o.product_code ? (
      <span className="font-mono text-green-700">{o.product_code}</span>
    ) : (
      <span className="text-gray-400 italic">not set</span>
    );

  const commonCells = (o) => (
    <>
      <td className="px-4 py-2 border-r font-medium whitespace-nowrap">
        {o.nme}
      </td>
      <td className="px-4 py-2 border-r whitespace-nowrap">{o.job_name}</td>
      <td className="px-4 py-2 border-r">
        {o.product_name ? (
          <span className="text-green-800">{o.product_name}</span>
        ) : (
          <span className="text-gray-400 italic">—</span>
        )}
      </td>
      <td className="px-4 py-2 border-r whitespace-nowrap text-gray-600">
        {o.pn_cust || "—"}
      </td>
      <td className="px-4 py-2 border-r whitespace-nowrap">{o.size}</td>
      <td className="px-4 py-2 border-r text-center">{o.roll ?? "—"}</td>
      <td className="px-4 py-2 border-r text-center">{o.std_pack ?? "—"}</td>
      <td className="px-4 py-2 border-r text-center font-bold bg-yellow-100 text-yellow-800">
        {o.qty}
      </td>
      <td className="px-4 py-2 border-r whitespace-nowrap">{o.po_num}</td>
      <td className="px-4 py-2 border-r whitespace-nowrap">
        {new Date(o.dlvr_date).toLocaleDateString()}
      </td>
      <td className="px-4 py-2 border-r whitespace-nowrap">{o.opr_name}</td>
      <td className="px-4 py-2 border-r">
        <select
          className="border border-gray-300 rounded px-1 py-0.5 text-xs bg-white min-w-[90px]"
          value={o.qc_id || ""}
          onChange={(e) => handleQCAssign(o.order_id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">Unassigned</option>
          {qcList
            .filter(
              (q) =>
                q.qc_name && q.qc_name.trim().toLowerCase() !== "unassigned",
            )
            .map((q) => (
              <option key={q.qc_id} value={q.qc_id}>
                {q.qc_name}
              </option>
            ))}
        </select>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">{productCell(o)}</td>
    </>
  );

  return (
    <>
      <div
        className="flex flex-col"
        style={{ maxHeight: "calc(100vh - 80px)" }}
      >
        <div className="w-full rounded-lg bg-yellow-500 mb-4 flex-shrink-0">
          <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
            QC Checking
          </label>
        </div>

        <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative">
          {isLoading && <LoadingOverlay message="Loading..." />}

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50 flex-shrink-0">
            {[
              {
                id: "on_process",
                label: "On Process",
                count: onProcess.length,
                activeColor: "border-green-500 text-green-700 bg-green-50",
                badgeColor: "bg-green-500",
              },
              {
                id: "qc_check",
                label: "QC Check",
                count: onQC.length,
                activeColor: "border-yellow-500 text-yellow-700 bg-yellow-50",
                badgeColor: "bg-yellow-500",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`px-6 py-3 text-xs font-bold uppercase tracking-wide flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? `border-b-2 ${tab.activeColor}`
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedIds(new Set());
                }}
              >
                {tab.label}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-bold text-white ${activeTab === tab.id ? tab.badgeColor : "bg-gray-300"}`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Hint / search bar */}
          <div className="flex-shrink-0 px-4 pt-2 pb-2">
            {activeTab === "on_process" ? (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={onProcessSearch}
                  onChange={(e) => setOnProcessSearch(e.target.value)}
                  placeholder="Search by customer, job name, part no., product, PO…"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-400"
                />
                {onProcessSearch && (
                  <button
                    className="text-xs text-gray-400 hover:text-gray-600"
                    onClick={() => setOnProcessSearch("")}
                  >
                    Clear
                  </button>
                )}
                <p className="text-xs text-gray-400 whitespace-nowrap">
                  Double-click a row to move to QC Check.
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Select rows and click{" "}
                <span className="font-semibold text-blue-600">
                  Process Selected
                </span>{" "}
                for batch, or click{" "}
                <span className="font-semibold text-blue-600">Process</span> on
                a single row.
              </p>
            )}
          </div>

          {/* Table area */}
          <div className="flex-1 min-h-0 overflow-auto">
            {activeTab === "on_process" && (
              <table className="text-sm w-full border-collapse">
                {sharedThead(false)}
                <tbody>
                  {onProcessFiltered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={14}
                        className="text-center py-8 text-gray-400 text-xs italic"
                      >
                        {onProcessSearch
                          ? "No orders match your search."
                          : "No orders in progress"}
                      </td>
                    </tr>
                  ) : (
                    onProcessFiltered.map((o, i) => (
                      <tr
                        key={o.order_id}
                        className={`text-xs border-b hover:bg-green-50 cursor-pointer ${stripedRow(i)}`}
                        onDoubleClick={() => setConfirmOrder(o)}
                        title="Double-click to move to QC Check"
                      >
                        {commonCells(o)}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {activeTab === "qc_check" && (
              <table className="text-sm w-full border-collapse">
                {sharedThead(true)}
                <tbody>
                  {onQC.length === 0 ? (
                    <tr>
                      <td
                        colSpan={15}
                        className="text-center py-8 text-gray-400 text-xs italic"
                      >
                        No orders in QC Check
                      </td>
                    </tr>
                  ) : (
                    onQC.map((o, i) => {
                      const partials = qcShipments.filter(
                        (s) => s.order_id === o.order_id,
                      );
                      return (
                        <React.Fragment key={o.order_id}>
                          <tr
                            className={`text-xs border-b hover:bg-yellow-50 ${stripedRow(i)} ${
                              selectedIds.has(o.order_id)
                                ? "ring-1 ring-inset ring-blue-400"
                                : ""
                            } ${isCheckboxDisabled(o) ? "opacity-40" : ""}`}
                          >
                            <td className="px-3 py-2 border-r text-center">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(o.order_id)}
                                onChange={() =>
                                  !isCheckboxDisabled(o) &&
                                  toggleSelect(o.order_id)
                                }
                                disabled={isCheckboxDisabled(o)}
                                className={
                                  isCheckboxDisabled(o)
                                    ? "cursor-not-allowed opacity-50"
                                    : "cursor-pointer"
                                }
                              />
                            </td>
                            {commonCells(o)}
                          </tr>
                          {partials.map((s, si) => (
                            <tr
                              key={s.transaction_id}
                              className="bg-indigo-50 border-b border-indigo-100 text-xs"
                            >
                              <td className="border-r bg-indigo-50"></td>
                              <td className="px-4 py-1.5 border-r text-indigo-500 font-medium whitespace-nowrap">
                                ↳ Partial #{si + 1}
                              </td>
                              <td className="px-3 py-1.5 border-r whitespace-nowrap">
                                <span className="text-gray-400 uppercase text-xs font-semibold mr-1">
                                  Transfer Date
                                </span>
                                <span className="text-gray-600">
                                  {new Date(
                                    s.transaction_time,
                                  ).toLocaleDateString()}
                                </span>
                              </td>
                              <td
                                colSpan={5}
                                className="px-3 py-1.5 border-r text-gray-500 italic"
                              >
                                {s.transaction_remark}
                              </td>
                              <td className="px-3 py-1.5 border-r text-center font-bold text-indigo-700 bg-indigo-100 whitespace-nowrap">
                                {s.stock_in}
                              </td>
                              <td
                                colSpan={2}
                                className="px-3 py-1.5 border-r text-gray-500 whitespace-nowrap"
                              >
                                by {s.transaction_by}
                              </td>
                              <td
                                colSpan={2}
                                className="px-3 py-1.5 border-r whitespace-nowrap text-gray-500"
                              >
                                {s.transaction_for}
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                {s.transaction_type ===
                                "QC Pending Stock In" ? (
                                  <span className="bg-yellow-100 text-yellow-700 border border-yellow-300 px-1.5 py-0.5 rounded text-xs font-medium">
                                    Pending
                                  </span>
                                ) : (
                                  <span className="bg-green-100 text-green-700 border border-green-300 px-1.5 py-0.5 rounded text-xs font-medium">
                                    Received
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Floating batch bar */}
      {activeTab === "qc_check" && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white rounded-xl shadow-2xl px-6 py-3 flex items-center gap-4 z-40">
          <span className="text-xs font-medium">
            {selectedIds.size} order{selectedIds.size > 1 ? "s" : ""} selected
          </span>
          <button
            className="bg-blue-500 hover:bg-blue-400 text-xs font-bold px-4 py-1.5 rounded-lg"
            onClick={() => setShowBatchModal(true)}
          >
            Process Selected
          </button>
          <button
            className="text-gray-400 hover:text-white text-xs underline"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Modals */}
      {confirmOrder && (
        <ConfirmQCModal
          order={confirmOrder}
          onConfirm={handleMoveToQC}
          onCancel={() => setConfirmOrder(null)}
        />
      )}
      {shipOrder && (
        <ShipModal
          order={shipOrder}
          onClose={() => setShipOrder(null)}
          onShipped={handleShipped}
          username={user?.username}
        />
      )}
      {showBatchModal && (
        <BatchModal
          orders={selectedOrders}
          onClose={() => setShowBatchModal(false)}
          onDone={(printData) => {
            handleShipped(printData);
            setSelectedIds(new Set());
          }}
          username={user?.username}
        />
      )}

      {/* QC shipment print queue */}
      {printQueue.map((p) => (
        <PrintQCShipment
          key={p.key}
          formId={p.formId}
          groupId={p.groupId}
          items={p.items}
          onDone={() =>
            setPrintQueue((prev) => prev.filter((q) => q.key !== p.key))
          }
          formNumber={formNos[FORM_KEYS.QC_TRANSFER]}
        />
      ))}
    </>
  );
}

export default QCChecking;
